// src/components/consulting/WyszukiwarkaDofinansowan.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Calendar, Euro, Building, MapPin, TrendingUp, ArrowRight, ChevronDown, X, Info } from 'lucide-react';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const WyszukiwarkaDofinansowan = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAmount, setSelectedAmount] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filteredGrants, setFilteredGrants] = useState([]);

  // Przykładowe dane dotacji
  const grants = [
    {
      id: 1,
      name: "Ścieżka SMART",
      category: "innowacje",
      region: "cała Polska",
      maxAmount: 3000000,
      cofinancing: 85,
      deadline: "2025-03-31",
      description: "Wsparcie na projekty B+R realizowane przez MŚP",
      requirements: ["MŚP", "Projekt B+R", "Innowacyjność produktu"],
      link: "/dotacje/sciezka-smart"
    },
    {
      id: 2,
      name: "Zielona Transformacja",
      category: "ekologia",
      region: "cała Polska",
      maxAmount: 5000000,
      cofinancing: 70,
      deadline: "2025-04-15",
      description: "Dofinansowanie na projekty związane z ochroną środowiska",
      requirements: ["Redukcja emisji CO2", "Plan transformacji", "Audyt energetyczny"],
      link: "#"
    },
    {
      id: 3,
      name: "Dotacje dla Mazowsza",
      category: "rozwój",
      region: "mazowieckie",
      maxAmount: 1000000,
      cofinancing: 75,
      deadline: "2025-02-28",
      description: "Wsparcie rozwoju przedsiębiorstw w województwie mazowieckim",
      requirements: ["Siedziba na Mazowszu", "Minimum 2 lata działalności", "Wzrost zatrudnienia"],
      link: "#"
    },
    {
      id: 4,
      name: "Innowacje Cyfrowe",
      category: "cyfryzacja",
      region: "cała Polska",
      maxAmount: 2000000,
      cofinancing: 80,
      deadline: "2025-05-30",
      description: "Wsparcie cyfrowej transformacji przedsiębiorstw",
      requirements: ["Plan cyfryzacji", "Wdrożenie min. 3 rozwiązań", "Szkolenia pracowników"],
      link: "#"
    },
    {
      id: 5,
      name: "Eksport Plus",
      category: "rozwój",
      region: "cała Polska",
      maxAmount: 800000,
      cofinancing: 65,
      deadline: "2025-06-15",
      description: "Wsparcie działalności eksportowej MŚP",
      requirements: ["Plan ekspansji", "Doświadczenie eksportowe", "Certyfikaty jakości"],
      link: "#"
    }
  ];

  const regions = [
    { value: 'all', label: 'Wszystkie regiony' },
    { value: 'cała Polska', label: 'Cała Polska' },
    { value: 'mazowieckie', label: 'Mazowieckie' },
    { value: 'małopolskie', label: 'Małopolskie' },
    { value: 'śląskie', label: 'Śląskie' },
    { value: 'wielkopolskie', label: 'Wielkopolskie' },
    { value: 'dolnośląskie', label: 'Dolnośląskie' }
  ];

  const categories = [
    { value: 'all', label: 'Wszystkie kategorie' },
    { value: 'innowacje', label: 'Innowacje i B+R' },
    { value: 'ekologia', label: 'Ekologia' },
    { value: 'cyfryzacja', label: 'Cyfryzacja' },
    { value: 'rozwój', label: 'Rozwój firmy' }
  ];

  const amounts = [
    { value: 'all', label: 'Dowolna kwota' },
    { value: '500000', label: 'Do 500 000 PLN' },
    { value: '1000000', label: 'Do 1 000 000 PLN' },
    { value: '3000000', label: 'Do 3 000 000 PLN' },
    { value: '5000000', label: 'Powyżej 3 000 000 PLN' }
  ];

  useEffect(() => {
    filterGrants();
  }, [searchTerm, selectedRegion, selectedCategory, selectedAmount]);

  const filterGrants = () => {
    let filtered = grants;

    // Filtrowanie po nazwie
    if (searchTerm) {
      filtered = filtered.filter(grant => 
        grant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grant.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrowanie po regionie
    if (selectedRegion !== 'all') {
      filtered = filtered.filter(grant => grant.region === selectedRegion);
    }

    // Filtrowanie po kategorii
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(grant => grant.category === selectedCategory);
    }

    // Filtrowanie po kwocie
    if (selectedAmount !== 'all') {
      const amount = parseInt(selectedAmount);
      if (amount === 5000000) {
        filtered = filtered.filter(grant => grant.maxAmount > 3000000);
      } else {
        filtered = filtered.filter(grant => grant.maxAmount <= amount);
      }
    }

    setFilteredGrants(filtered);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedRegion('all');
    setSelectedCategory('all');
    setSelectedAmount('all');
  };

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Euro className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Search className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Wyszukiwarka Dofinansowań
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
              Znajdź idealne dofinansowanie dla swojej firmy. Przeszukaj dostępne programy dotacyjne i odkryj możliwości rozwoju.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="py-8 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Search Bar */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Szukaj dotacji po nazwie lub słowach kluczowych..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Filter className="w-5 h-5" />
              <span>Filtry</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-white/5 rounded-lg border border-white/10">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Region</label>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                >
                  {regions.map(region => (
                    <option key={region.value} value={region.value}>{region.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Kategoria</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Kwota dofinansowania</label>
                <select
                  value={selectedAmount}
                  onChange={(e) => setSelectedAmount(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                >
                  {amounts.map(amount => (
                    <option key={amount.value} value={amount.value}>{amount.label}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3 flex justify-end">
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 text-violet-500 hover:text-violet-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Wyczyść filtry
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">
              Znaleziono {filteredGrants.length} {filteredGrants.length === 1 ? 'dotację' : filteredGrants.length < 5 ? 'dotacje' : 'dotacji'}
            </h2>
          </div>

          {filteredGrants.length > 0 ? (
            <div className="grid gap-6">
              {filteredGrants.map(grant => (
                <div key={grant.id} className="group backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2">{grant.name}</h3>
                          <p className="text-gray-400">{grant.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <MapPin className="w-4 h-4" />
                            Region
                          </div>
                          <p className="text-white font-medium">{grant.region}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <Euro className="w-4 h-4" />
                            Max. kwota
                          </div>
                          <p className="text-white font-medium">{grant.maxAmount.toLocaleString('pl-PL')} PLN</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <TrendingUp className="w-4 h-4" />
                            Dofinansowanie
                          </div>
                          <p className="text-white font-medium">{grant.cofinancing}%</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <Calendar className="w-4 h-4" />
                            Termin
                          </div>
                          <p className="text-white font-medium">{new Date(grant.deadline).toLocaleDateString('pl-PL')}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2">Główne wymagania:</p>
                        <div className="flex flex-wrap gap-2">
                          {grant.requirements.map((req, index) => (
                            <span key={index} className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-300">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <Link
                        to={grant.link}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-violet-600/25 transition-all duration-300"
                      >
                        Szczegóły
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={handleContactScroll}
                        className="px-6 py-3 bg-white/5 border border-white/20 rounded-lg text-white font-medium hover:bg-white/10 transition-all duration-300"
                      >
                        Zapytaj o dotację
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/5 rounded-full mb-4">
                <Info className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Nie znaleziono dotacji</h3>
              <p className="text-gray-400 mb-6">Spróbuj zmienić kryteria wyszukiwania</p>
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-2 text-violet-500 hover:text-violet-400 transition-colors"
              >
                <X className="w-4 h-4" />
                Wyczyść filtry
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            Potrzebujesz pomocy w wyborze dotacji?
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Nasi eksperci pomogą Ci znaleźć najlepsze dofinansowanie dla Twojego projektu
          </p>
          <button 
            onClick={handleContactScroll}
            className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
          >
            <span>Bezpłatna konsultacja</span>
            <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
          </button>
        </div>
      </section>

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default WyszukiwarkaDofinansowan;